import { memo, MouseEvent, useCallback, useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import { RoomTypes } from '@weblite/ts-action/room'
import { Waypoint } from 'react-waypoint'
import { RoomListItem, WorkspaceType } from '$logic/room-list'
import {
  INCREASE_LIMIT_OFFSET,
  useItemsRenderLimit,
} from '$comp/_app/components/leftPanel/components/chat-list/react-hooks'
import { openRoom, useOpenedRoomId } from '$logic/opened-room'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { DEFAULT_WORKSPACE_ID, openWorkspace } from '$logic/workspace'
import { ChatListItem } from '$common/chat-list-item-new'
import { ChatSelectionTabTypes } from '$comp/_app/components/leftPanel/components/chat-list/chat-list'
import { WorkspaceChatListItem } from '$common/chat-list-item-new/workspace-chat-list-item'

const filterByRoomType = (
  roomType: RoomTypes | 'workspace' | undefined,
  type: ChatSelectionTabTypes,
) =>
  type === 'ALL' ||
  (type === 'PV' && roomType === 'PRIVATE_SPACE') ||
  type === roomType

export const ListRenderer = ({
  selectionMap,
  selectionCount,
  items,
  onItemSelectedChange,
  chatType,
  visited,
  workspaceId,
  onUpdateHeight,
}: {
  selectionCount: number
  selectionMap: Record<string, boolean>
  items: RoomListItem[]
  onItemSelectedChange: (selected: boolean, chatId: string) => void
  chatType?: ChatSelectionTabTypes
  visited: boolean
  workspaceId?: string
  onUpdateHeight?: () => void
}) => {
  const actualItems = (visited ? items : []) as RoomListItem[]
  const selectOnTapRef = useRef<boolean>(false)
  const selectionMapRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    selectionMapRef.current = selectionMap
  }, [selectionMap])

  useEffect(() => {
    selectOnTapRef.current = selectionCount > 0
  }, [selectionCount])

  const { itemsRenderLimit, increaseItemsRenderLimit, increaseNeeded } =
    useItemsRenderLimit({
      totalCount: actualItems?.length ?? 0,
      calculatePermissionsOnMount:
        chatType === 'ALL' || workspaceId === DEFAULT_WORKSPACE_ID,
      calculatePermissionsOnScroll: false,
    })

  const onClick = useCallback(
    (id: string, type: RoomListItem['type']) => {
      const selected = selectionMapRef.current[id]

      if (selected) onItemSelectedChange(false, id)
      else if (selectOnTapRef.current) onItemSelectedChange(true, id)
      else if (type === WorkspaceType) openWorkspace(id)
      else openRoom(id)
    },
    [onItemSelectedChange],
  )

  const onContextMenu = useCallback(
    (chatId: string) => (e: MouseEvent) => {
      e.preventDefault()
      if (!selectOnTapRef.current) onItemSelectedChange(true, chatId)
    },
    [onItemSelectedChange],
  )

  const handleLongPress = useCallback(
    (chatId: string) => () => {
      if (!selectOnTapRef.current) onItemSelectedChange(true, chatId)
    },
    [onItemSelectedChange],
  )

  const isDesktopVersion = useIsDesktopSize()
  const openedRoomId = useOpenedRoomId()

  useEffect(() => {
    onUpdateHeight?.()
  }, [itemsRenderLimit, onUpdateHeight])

  return (
    // <FlipMove enterAnimation="fade" leaveAnimation="fade">
    <div style={{ minHeight: '70px' }}>
      {actualItems
        ?.filter(item =>
          chatType ? filterByRoomType(item.type, chatType) : item,
        )
        .slice(0, itemsRenderLimit)
        .map((chatItem, index) => (
          <ChatListItemWrapper key={chatItem.chatId}>
            {increaseNeeded &&
              index === itemsRenderLimit - INCREASE_LIMIT_OFFSET - 1 && (
                <Waypoint onEnter={increaseItemsRenderLimit} />
              )}

            {chatItem.type === WorkspaceType ? (
              <WorkspaceChatListItem
                {...chatItem}
                workspaceId={chatItem.chatId}
                key={chatItem.chatId}
                selected={selectionMap[chatItem.chatId]}
                onClick={onClick}
                isOpened={isDesktopVersion && openedRoomId === chatItem.chatId}
                onContextMenu={onContextMenu}
                onLongPress={handleLongPress}
              />
            ) : (
              <ChatListItem
                {...chatItem}
                chatId={chatItem.chatId}
                key={chatItem.chatId}
                selected={selectionMap[chatItem.chatId]}
                isOpened={isDesktopVersion && openedRoomId === chatItem.chatId}
                onClick={onClick}
                onContextMenu={onContextMenu}
                onLongPress={handleLongPress}
                workspaceId={chatItem.workspaceId}
              />
            )}
          </ChatListItemWrapper>
        ))}
    </div>
    //  </FlipMove>
  )
}

const ChatListItemWrapper = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral000};
`
